import type { TFunction } from 'i18next';
import type { Contract } from '../types';

/**
 * The ÄTA number of each scoped amendment seen in a contract list, by
 * contract id. A scoped amendment is stored as a new plan version of its
 * contract, but other versions, such as a replacement agreement after a
 * conflict, are not ÄTAs and must not shift the number (ClickUp 869fgj5tr).
 */
const amendmentNumbers = new Map<string, number>();

/**
 * Number the scoped amendments of each contract chain: the version less the
 * later versions that are not ÄTAs, such as a replacement agreement. Versions
 * missing from the list leave the number as the version gives it.
 */
export function rememberAgreementNumbers(contracts: Contract[]): void {
  const chainKey = (contract: Contract) => contract.contractChainId ?? contract.id;
  const replacementVersions = new Map<string, number[]>();
  for (const contract of contracts) {
    if (contract.stageVariation || (contract.contractVersion ?? 1) <= 1) continue;
    replacementVersions.set(chainKey(contract), [...(replacementVersions.get(chainKey(contract)) ?? []), contract.contractVersion ?? 1]);
  }
  for (const contract of contracts) {
    if (!contract.stageVariation) continue;
    const version = Math.max(contract.contractVersion ?? 2, 2);
    const replacementsBefore = (replacementVersions.get(chainKey(contract)) ?? []).filter(other => other < version).length;
    amendmentNumbers.set(contract.id, version - 1 - replacementsBefore);
  }
}

/** A scoped amendment is persisted as a cumulative plan version. Its public
 * identity is an ÄTA on the original contract, not a new main contract. */
export function agreementVariationNumber(contract: Contract): number | null {
  return contract.stageVariation
    ? amendmentNumbers.get(contract.id) ?? Math.max(contract.contractVersion ?? 2, 2) - 1
    : contract.contractType === 'variation'
      ? contract.variationNumber ?? contract.contractNumber
      : null;
}

export function agreementRoot(contract: Contract, contracts: Contract[]): Contract {
  let current = contract;
  const seen = new Set([current.id]);
  while (true) {
    const parentId = current.stageVariation?.originalContractId ?? current.replacesContractId ?? current.parentContractId;
    const parent = contracts.find(item => item.id === parentId && item.projectId === contract.projectId);
    if (!parent || seen.has(parent.id)) break;
    seen.add(parent.id);
    current = parent;
  }
  return contracts.find(item => item.id !== contract.id && item.id === contract.contractChainId && item.projectId === contract.projectId) ?? current;
}

export function agreementLabel(contract: Contract, contracts: Contract[], t: TFunction): string {
  const variation = agreementVariationNumber(contract);
  if (variation === null) return t('variationWizard.contractNumber', { number: contract.contractNumber });
  const root = agreementRoot(contract, contracts);
  return root.id !== contract.id
    ? t('variationWizard.amendmentContract', { number: variation, contract: root.contractNumber })
    : t('variationWizard.originVariation', { number: variation });
}

export function agreementBasis(contract: Contract, contracts: Contract[], t: TFunction): string | null {
  if (agreementVariationNumber(contract) === null) return null;
  const root = agreementRoot(contract, contracts);
  if (root.id === contract.id) return null;
  const parentId = contract.stageVariation?.originalContractId ?? contract.replacesContractId ?? contract.parentContractId;
  const parent = contracts.find(item => item.id === parentId && item.projectId === contract.projectId);
  const previous = parent && agreementVariationNumber(parent);
  return previous != null
    ? t('variationWizard.previousBasis', { number: previous, contract: root.contractNumber })
    : t('variationWizard.basis', { number: root.contractNumber });
}

/** Where a contract's document folder sits in the library: an ÄTA under the
 * contract it changes, with its ÄTA number (ClickUp 869fmhd6p). Folders are
 * keyed by contract number, so a root sharing the ÄTA's number is ignored. */
export interface ContractFolderInfo {
  contractNumber: number;
  parentContractNumber: number | null;
  variationNumber: number | null;
}

export function contractFolderHierarchy(contracts: Contract[]): ContractFolderInfo[] {
  return contracts.map(contract => {
    const variationNumber = agreementVariationNumber(contract);
    const root = variationNumber === null ? contract : agreementRoot(contract, contracts);
    const parentContractNumber = root.id !== contract.id && root.contractNumber !== contract.contractNumber
      ? root.contractNumber
      : null;
    return { contractNumber: contract.contractNumber, parentContractNumber, variationNumber };
  });
}
